import { cn } from '@latitude/token-utils';
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  ElementType,
  Ref,
} from 'react';

import { PADDING, PADDING_X, PADDING_Y, type SpacingStep } from './spacing';

type BoxOwnProps<E extends ElementType> = {
  as?: E;
  padding?: SpacingStep;
  paddingX?: SpacingStep;
  paddingY?: SpacingStep;
  ref?: Ref<ComponentRef<E>>;
};

export type BoxProps<E extends ElementType = 'div'> = BoxOwnProps<E> &
  Omit<ComponentPropsWithoutRef<E>, keyof BoxOwnProps<E>>;

// Internal base primitive — not exported from the package's public entry
// point (src/index.ts). Stack/Inline/Grid/GridItem compose this; promote to
// public only if a real external need appears (design.md D1).
export const Box = <E extends ElementType = 'div'>({
  as,
  padding,
  paddingX,
  paddingY,
  className,
  ref,
  ...rest
}: BoxProps<E>) => {
  const Component = (as ?? 'div') as ElementType;

  return (
    <Component
      ref={ref}
      className={cn(
        padding !== undefined && PADDING[padding],
        paddingX !== undefined && PADDING_X[paddingX],
        paddingY !== undefined && PADDING_Y[paddingY],
        className,
      )}
      {...rest}
    />
  );
};

Box.displayName = 'Box';
